import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../../api/axios';

type UserItem = {
  user_coupon_id: number;
  bound_at: string;
  usage_count: number; // このクーポンでの支払回数
  total_paid: number; // 支払金額合計
  commission: number; // 返佣金額
  user: {
    id: number;
    email: string;
    name?: string | null;
    phone?: string | null;
    role?: string | null;
  };
  status: string; // 'disabled' | 'used' | 'pending' など
};

type UsersResp = {
  total: number;
  items: UserItem[];
  page: number;
  page_size: number;
  summary?: {
    commission_type: string | null;
    commission_value: number | null;
    total_commission: number;
  };
};

interface Props {
  couponId: number | null;
  couponName?: string | null;
  couponNote?: string | null;
  open: boolean;
  onClose: () => void;
}

const PAGE_SIZES = [20, 50, 100];

const AdminCouponUsersDialog: React.FC<Props> = ({ couponId, couponName, couponNote, open, onClose }) => {
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rows, setRows] = useState<UserItem[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<UsersResp['summary']>(undefined);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const dtf = useMemo(
    () =>
      new Intl.DateTimeFormat(
        i18n.language === 'zh' ? 'zh-CN' : 'ja-JP',
        { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Tokyo' }
      ),
    [i18n.language]
  );

  useEffect(() => {
    if (!open || !couponId) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const { data } = await axios.get<UsersResp>(`/admin/coupons/${couponId}/users`, {
          params: { page, page_size: pageSize },
        });
        if (cancelled) return;
        setRows(data.items || []);
        setTotal(data.total || 0);
        setSummary(data.summary);
      } catch (e: any) {
        if (cancelled) return;
        setErr(
          e?.response?.data?.detail ||
          (t('admin.coupons.errors.fetch_failed') as string) ||
          '取得に失敗しました'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, couponId, page, pageSize, t]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-xl p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {t('admin.coupons.users_dialog.title', { name: couponName || '' }) || 'バインド済みユーザー'}
            {couponNote && <span className="text-sm text-gray-500 ml-2">（{couponNote}）</span>}
          </h3>
          <button
            onClick={onClose}
            className="px-3 py-1.5 border rounded hover:bg-gray-50 text-sm"
          >
            {t('admin.coupons.users_dialog.close') || '閉じる'}
          </button>
        </div>

        {/* Summary */}
        {summary && summary.commission_type && (
          <div className="mb-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-gray-700">
                  {t('admin.coupons.users_dialog.commission_summary') || '返佣合計'}
                </span>
                <span className="ml-2 text-xs text-gray-500">
                  ({summary.commission_type === 'percent'
                    ? `${summary.commission_value}%`
                    : `¥${summary.commission_value} × ${t('admin.coupons.users_dialog.per_payment') || '支払回数'}`})
                </span>
              </div>
              <div className="text-2xl font-bold text-emerald-600">
                ¥{summary.total_commission?.toLocaleString() || 0}
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-gray-600">
            {total.toLocaleString()}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">{t('admin.common.page_size')}</label>
            <select
              className="border rounded px-2 py-1 text-sm"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="text-sm text-gray-600">{t('admin.coupons.users_dialog.loading')}</div>
        ) : err ? (
          <div className="text-sm text-red-600">{err}</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-gray-600">{t('admin.coupons.users_dialog.empty')}</div>
        ) : (
          <div className="overflow-auto rounded-xl border">
            {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み） */}
            <div className="md:hidden divide-y">
              {rows.map((r) => {
                // status: 'disabled'=クーポン無効, 'used'=支払済み, 'pending'=未利用
                const statusLabel =
                  r.status === 'disabled'
                    ? (t('admin.coupons.status.disabled') || '無効')
                    : r.status === 'used'
                      ? (t('admin.coupons.status.used') || '利用済み')
                      : (t('admin.coupons.status.pending') || '未利用');
                const statusClass =
                  r.status === 'disabled'
                    ? 'bg-gray-100 text-gray-500'
                    : r.status === 'used'
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-yellow-50 text-yellow-700';

                return (
                  <div key={r.user_coupon_id} className="p-4">
                    {/* 氏名 + 状態 */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold break-words">{r.user.name || '—'}</div>
                        <div className="text-xs text-gray-400 mt-0.5">ID: {r.user.id}</div>
                      </div>
                      <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs ${statusClass}`}>
                        {statusLabel}
                      </span>
                    </div>

                    {/* 各フィールド（ラベル：値） */}
                    <div className="mt-3 space-y-1.5 text-xs">
                      <div className="flex gap-1 min-w-0">
                        <span className="text-gray-400 shrink-0">{t('admin.coupons.users_dialog.columns.email')}：</span>
                        <span className="text-gray-700 break-all min-w-0">{r.user.email}</span>
                      </div>
                      <div className="flex gap-1 min-w-0">
                        <span className="text-gray-400 shrink-0">{t('admin.coupons.users_dialog.columns.phone') || '電話番号'}：</span>
                        <span className="text-gray-700 break-all min-w-0">{r.user.phone || '—'}</span>
                      </div>
                      <div className="flex gap-1 min-w-0">
                        <span className="text-gray-400 shrink-0">{t('admin.coupons.users_dialog.columns.bound_at')}：</span>
                        <span className="text-gray-700 break-words min-w-0">
                          {r.bound_at ? dtf.format(new Date(r.bound_at)) : '—'}
                        </span>
                      </div>
                    </div>

                    {/* 集計（支払回数 / 支払金額 / 返佣金額） */}
                    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                        <span className="text-gray-400">{t('admin.coupons.users_dialog.columns.payments_count')}：</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">
                          {r.usage_count ?? 0}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                        <span className="text-gray-400">{t('admin.coupons.users_dialog.columns.total_paid')}：</span>
                        <span className="text-gray-700 font-medium break-all">¥{(r.total_paid || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                        <span className="text-gray-400">{t('admin.coupons.users_dialog.columns.commission') || '返佣金額'}：</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold break-all">
                          ¥{(r.commission || 0).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ▼ デスクトップ: テーブル表示 */}
            <table className="min-w-full text-sm hidden md:table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">ID</th>
                  <th className="px-3 py-2 text-left">{t('admin.coupons.users_dialog.columns.name')}</th>
                  <th className="px-3 py-2 text-left">{t('admin.coupons.users_dialog.columns.email')}</th>
                  <th className="px-3 py-2 text-left">{t('admin.coupons.users_dialog.columns.phone') || '電話番号'}</th>
                  <th className="px-3 py-2 text-left">{t('admin.coupons.users_dialog.columns.bound_at')}</th>
                  <th className="px-3 py-2 text-right">{t('admin.coupons.users_dialog.columns.payments_count')}</th>
                  <th className="px-3 py-2 text-right">{t('admin.coupons.users_dialog.columns.total_paid')}</th>
                  <th className="px-3 py-2 text-left">
                    {t('admin.coupons.users_dialog.columns.status') || '状態'}
                  </th>
                  <th className="px-3 py-2 text-right">
                    {t('admin.coupons.users_dialog.columns.commission') || '返佣金額'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  // status: 'disabled'=クーポン無効, 'used'=支払済み, 'pending'=未利用
                  const statusLabel =
                    r.status === 'disabled'
                      ? (t('admin.coupons.status.disabled') || '無効')
                      : r.status === 'used'
                        ? (t('admin.coupons.status.used') || '利用済み')
                        : (t('admin.coupons.status.pending') || '未利用');
                  const statusClass =
                    r.status === 'disabled'
                      ? 'bg-gray-100 text-gray-500'
                      : r.status === 'used'
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-yellow-50 text-yellow-700';

                  return (
                    <tr key={r.user_coupon_id} className="border-t">
                      <td className="px-3 py-2">{r.user.id}</td>
                      <td className="px-3 py-2">{r.user.name || '—'}</td>
                      <td className="px-3 py-2">{r.user.email}</td>
                      <td className="px-3 py-2">{r.user.phone || '—'}</td>
                      <td className="px-3 py-2">
                        {r.bound_at ? dtf.format(new Date(r.bound_at)) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-indigo-50 text-indigo-700">
                          {r.usage_count ?? 0}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-medium">
                        ¥{(r.total_paid || 0).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-left">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-emerald-50 text-emerald-700 font-semibold">
                          ¥{(r.commission || 0).toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pager */}
        <div className="flex items-center justify-between mt-3">
          <div className="text-sm">{t('admin.common.page')} {page}</div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              {t('admin.common.prev')}
            </button>
            <button
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
              disabled={rows.length < pageSize}
              onClick={() => setPage(p => p + 1)}
            >
              {t('admin.common.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCouponUsersDialog;
